import React from "react";
import {CheckMobile} from "../../../AppSystem/Helpers/CheckMobile";

/*
Simple table for detail sections: one header row instead of repeating the labels
on every item.

- columns: [{label, align: 'right', nowrap, strong, width, role}]
  role marks the value that leads the mobile card ('title'), its amount
  ('amount') and its date ('date').
  Declaring widths makes the layout fixed, so two tables sharing the same
  columns line up with each other.
- rows: an array of rows, each one an array of cells matching the columns

On a phone each row becomes one of the list's cards, so the table never has to
scroll sideways.
*/
export function DetailTable({columns, rows}) {
    const isMobile = CheckMobile(998);

    /*
    Mobile: the compact card the list uses. The columns say which value leads the
    card, which is the amount and which is the date; the rest follow as secondary
    lines, so a row never turns into a tall stack of labels.

    Those lines span the whole card with the label on the left and the value on
    the right, so every figure of a card is read down the same edge as the
    amount instead of trailing behind labels of different lengths.
    */
    if (isMobile) {
        const roleIndex = (role) => columns.findIndex(column => column.role === role);
        const titleIndex = roleIndex('title') >= 0 ? roleIndex('title') : 0;
        const amountIndex = roleIndex('amount') >= 0 ? roleIndex('amount') : columns.length - 1;
        const dateIndex = roleIndex('date');
        const secondaryIndexes = columns
            .map((column, index) => index)
            .filter(index => index !== titleIndex && index !== amountIndex && index !== dateIndex);

        return (
            <div className="reimbursement-cards-list ds-mobile-cards">
                {rows.map((row, rowIndex) => (
                    <div key={`card${rowIndex}`} className="reimbursement-card-item ds-mobile-card">
                        <div className="reimbursement-card-content-row">
                            <div className="reimbursement-card-left">
                                <div className="reimbursement-card-purpose">{row[titleIndex]}</div>
                            </div>

                            <div className="reimbursement-card-right">
                                <div className="reimbursement-card-right-top">
                                    <div className="reimbursement-card-amount reimbursement-card-amount-positive">
                                        {row[amountIndex]}
                                    </div>
                                </div>
                                {dateIndex >= 0 && row[dateIndex] ?
                                    <div className="reimbursement-card-date">{row[dateIndex]}</div>
                                : ''}
                            </div>
                        </div>

                        {secondaryIndexes.map(index => (
                            row[index] === '' || row[index] === null || row[index] === undefined ? null :
                                <div key={`line${rowIndex}-${index}`} className="reimbursement-card-org ds-mobile-line">
                                    <span className="ds-mobile-label">{columns[index]?.label}:</span>
                                    <span className="ds-mobile-value">{row[index]}</span>
                                </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    const cellClass = (column, extra = '') => [
        extra,
        column?.align === 'right' ? 'ds-cell-right' : '',
        column?.nowrap ? 'ds-nowrap' : '',
    ].filter(Boolean).join(' ');

    const hasWidths = columns.some(column => column.width);

    return (
        <div className="sl-wrap">
            <div className="sl-card-shell">
                <div className="ds-table-scroll">
                    <table className={`sl-table sl-table-layout ${hasWidths ? 'ds-table-fixed' : ''}`}>
                        {hasWidths ?
                            <colgroup>
                                {columns.map((column, index) => (
                                    <col key={`col${index}`} style={{width: column.width}} />
                                ))}
                            </colgroup>
                        : ''}
                        <thead>
                            <tr className="sl-table-head-row">
                                {columns.map((column, index) => (
                                    <th key={`th${index}`} className={cellClass(column, 'sl-th-base sl-th-no-sort')}>
                                        <span className="sl-th-label-static">{column.label}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr
                                    key={`tr${rowIndex}`}
                                    className={`sl-row ${rowIndex < rows.length - 1 ? 'sl-row-divider' : ''}`}
                                >
                                    {row.map((cell, cellIndex) => (
                                        <td
                                            key={`td${rowIndex}-${cellIndex}`}
                                            className={cellClass(
                                                columns[cellIndex],
                                                `sl-td-base ${columns[cellIndex]?.strong ? 'ds-cell-strong' : ''}`
                                            )}
                                        >
                                            {cell}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
